import React, { useEffect, useState } from 'react';

/**
 * A person's avatar wherever it appears — top bar, sidebar, lists.
 *
 * The profile page had the photo and nothing else did, so the same person was
 * a picture in one corner of the screen and two letters in another. This is
 * the one component that knows how to draw an avatar.
 *
 * It asks for the image rather than first asking whether there is one. The
 * endpoint answers 404 when the person has no photo, and the error handler
 * below falls back to initials — one request instead of two, and no state to
 * keep in step.
 */

const API = process.env.REACT_APP_API_URL || '/api';

/** Bumped when a photo is uploaded or removed, to break the browser's cache. */
export const PHOTO_CHANGED = 'tychi-photo-changed';
export const notifyPhotoChanged = () => window.dispatchEvent(new Event(PHOTO_CHANGED));

const UserAvatar = ({
  employeeId = null,          // null = whoever is signed in
  initials = '??',
  className = '',
  alt = '',
}) => {
  const [failed, setFailed] = useState(false);
  const [v, setV] = useState(0);

  useEffect(() => {
    const onChange = () => { setFailed(false); setV((n) => n + 1); };
    window.addEventListener(PHOTO_CHANGED, onChange);
    return () => window.removeEventListener(PHOTO_CHANGED, onChange);
  }, []);

  // A new photo for the same person is the same URL, which the browser would
  // happily serve from cache. The counter makes it a different one.
  const src = `${API}/profile/${employeeId || 'me'}/photo${v ? `?v=${v}` : ''}`;

  if (failed) return <div className={className}>{initials}</div>;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{ objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  );
};

export default UserAvatar;
